<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref, type Component, type Ref } from 'vue'

import AnimatedXButton from '@/components/ui/AnimatedXButton.vue'
import { useBottomSheet } from '@/composables/useBottomSheet'
import { useEventListener } from '@/composables/useEventListener'
import {
  FLOATING_SHEET_BOTTOM_OFFSET,
  mobileSheetFullHeight,
  mobileSheetMaxHeight,
} from '@/services/safeAreaService'
import { Z_INDEX } from '@/utils/zIndex'

/**
 * Mobile "map tool" drawer (Map type / Map layers / Map content) as a non-modal, draggable bottom
 * sheet — the same family as PoiBrowseSheet / DetailSheet. Non-modal on purpose: changing the
 * basemap, toggling overlays or POI categories updates the live map behind the sheet (a dimmed modal
 * would hide the very thing you're configuring), and you can drag/swipe it down to see more map.
 * Mirrors the desktop docked panels. Title + icon are per-tool; content goes in the default slot.
 */
defineOptions({ name: 'MapToolSheet' })

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    icon?: Component
    testid?: string
    /** Height the sheet opens at. Short tools (e.g. Map type) open compact at 'peek'. */
    initialMode?: 'peek' | 'mid' | 'full'
  }>(),
  { initialMode: 'mid' },
)
const emit = defineEmits<{ close: [] }>()

// Same bottom offset every other detail sheet uses (sits at the screen edge when the tab bar is
// hidden at collection/detail level).
const floatingSheetBottom = inject<Ref<string>>(
  'floatingSheetBottomOffset',
  ref(FLOATING_SHEET_BOTTOM_OFFSET),
)

const viewportHeight = ref(
  typeof window === 'undefined' ? 844 : (window.visualViewport?.height ?? window.innerHeight),
)
const updateViewportHeight = () => {
  viewportHeight.value = window.visualViewport?.height ?? window.innerHeight
}

const sheetHeights = computed<Record<'peek' | 'mid' | 'full', number>>(() => {
  const height = viewportHeight.value
  const full = mobileSheetFullHeight(height)
  return {
    peek: Math.min(220, full - 40),
    mid: Math.min(460, full - 40),
    full,
  }
})
const sheet = useBottomSheet({
  modes: ['peek', 'mid', 'full'] as const,
  heights: sheetHeights,
  initialMode: props.initialMode,
  // Swipe the sheet down past peek to dismiss (as well as the header close button).
  onDismiss: () => emit('close'),
})

const sheetStyle = computed(() => ({
  height: `${sheet.height.value}px`,
  maxHeight: mobileSheetMaxHeight(floatingSheetBottom.value),
  bottom: floatingSheetBottom.value,
  zIndex: String(Z_INDEX.MOBILE_TOOLBAR),
  transition: sheet.isDragging.value ? 'none' : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

onMounted(() => window.visualViewport?.addEventListener('resize', updateViewportHeight))
onBeforeUnmount(() => window.visualViewport?.removeEventListener('resize', updateViewportHeight))
useEventListener(window, 'resize', updateViewportHeight)
</script>

<template>
  <Transition name="map-tool-sheet">
    <div
      v-if="props.open"
      class="pointer-events-auto fixed left-3 right-3 flex flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.14)]"
      :style="sheetStyle"
      :data-testid="props.testid"
    >
      <!-- Drag handle + header are the drag surface (the close button is excluded by useBottomSheet). -->
      <div
        class="shrink-0 touch-none bg-white"
        @pointerdown="sheet.onPointerDown"
        @pointermove="sheet.onPointerMove"
        @pointerup="sheet.onPointerUp"
        @pointercancel="sheet.onPointerUp"
      >
        <div class="flex justify-center pt-2 pb-1">
          <div class="h-1.5 w-12 rounded-full bg-gray-300"></div>
        </div>
        <div class="relative flex min-h-11 items-center justify-center px-12">
          <div class="flex min-w-0 items-center gap-2">
            <component :is="props.icon" v-if="props.icon" class="h-4 w-4 shrink-0 text-gray-600" />
            <h2 class="truncate text-base font-semibold tracking-tight text-gray-900">
              {{ props.title }}
            </h2>
          </div>
          <AnimatedXButton
            class="absolute top-1/2 right-1.5 -translate-y-1/2"
            data-testid="map-tool-sheet-close"
            @click="emit('close')"
          />
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <slot />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.map-tool-sheet-enter-active,
.map-tool-sheet-leave-active {
  transition:
    transform 200ms ease,
    opacity 200ms ease;
}
.map-tool-sheet-enter-from,
.map-tool-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .map-tool-sheet-enter-active,
  .map-tool-sheet-leave-active {
    transition: none;
  }
}
</style>
